import React from 'react'
import { CalendarOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { ICellRendererParams } from 'ag-grid-enterprise'
import { useDateFormatter } from '../../../../hooks/date/useDateFormatter'
import { DateFormat } from '../../../../../config/dayjsConfig'

interface DateRangeCellRendererParams extends ICellRendererParams {
  dateFormat?: keyof DateFormat
}

/**
 * Cell renderer for date range fields
 * Displays formatted date range with calendar icon using localized format
 * Always shows calendar icon, even when empty
 *
 * Params:
 * - dateFormat: Optional date format key (default: 'tableMonth')
 *
 * Example usage:
 * ```typescript
 * {
 *   cellRenderer: DateRangeCellRenderer,
 *   cellRendererParams: {
 *     dateFormat: 'standard' // or 'tableMonth' or 'long'
 *   }
 * }
 * ```
 */
export const DateRangeCellRenderer: React.FC<DateRangeCellRendererParams> = ({ value, dateFormat = 'standard' }) => {
  const dateFormatter = useDateFormatter()

  if (!value || !value.startPeriod || !value.endPeriod) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          width: '100%',
          height: '100%',
          paddingRight: 4
        }}
      >
        <CalendarOutlined style={{ fontSize: 12, opacity: 0.45 }} />
      </div>
    )
  }

  const startDate = dayjs(`${value.startPeriod.year}-${String(value.startPeriod.month).padStart(2, '0')}-01`)
  const endDate = dayjs(`${value.endPeriod.year}-${String(value.endPeriod.month).padStart(2, '0')}-01`)

  const formattedStart = dateFormatter(startDate, dateFormat)
  const formattedEnd = dateFormatter(endDate, dateFormat)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        height: '100%',
        paddingRight: 4,
        gap: 6
      }}
    >
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1
        }}
      >
        {`${formattedStart} - ${formattedEnd}`}
      </span>
      <CalendarOutlined style={{ fontSize: 12, opacity: 0.45, flexShrink: 0 }} />
    </div>
  )
}
